import { useState } from 'react'
import { Trans } from 'react-i18next'
import { PageTitle } from '../components/atoms/PageTitle'
import { Box, Button, Stack } from '@mui/material'
import { CalendarTimeRange, Device, DeviceHwID, FixedCalendarRuleParams, Rule, TimeHHMM, WeekdaysToggles } from '../types/models'
import { TimeEdit } from './atoms/TimeEdit'
import { DaySelector } from './atoms/DaySelector'
import { IntervalPanel } from './IntervalPanel'
import { isDebug } from '../utils/misc'
import { rangeMerge, visual2intervals } from '../utils/time'
import clone from 'clone'
import { RelayPort } from '../types/ports'
import { callAxios } from '../hooks/useAxios'
import { isErrorResponse } from '../types/api'
import { AppContextProps } from '../hooks/useApp'
import { DeviceUpdate } from '../classes/DeviceCollection'

export interface RuleEditFixedCalendarProps {
  rule: Rule
  port: RelayPort
  device: Device
  app: AppContextProps
  addRule: (hwId: DeviceHwID, rule: Rule) => void
  onUpdate: (params: DeviceUpdate) => void
}

export function RuleEditFixedCalendar(props: RuleEditFixedCalendarProps): React.ReactElement {

  const { rule, port, device, app } = props

  const [ruleParams, setRuleParams] = useState<FixedCalendarRuleParams>(rule.params as FixedCalendarRuleParams)
  const [editInterval, setEditInterval] = useState<CalendarTimeRange | null>(null)
  const [editIntervalIndex, setEditIntervalIndex] = useState<number | null>(null)

  if (ruleParams.visuals === undefined) {
    setRuleParams({ ...ruleParams, visuals: [] })
    return <></>
  }

  const onAddInterval = () => {
    ruleParams.visuals.push({
      start: '00:00' as TimeHHMM,
      stop: '00:00' as TimeHHMM,
      weekdays: [true, true, true, true, true, true, true]
    })
    setEditInterval(ruleParams.visuals[ruleParams.visuals.length - 1])
    setEditIntervalIndex(ruleParams.visuals.length - 1)
  }

  const visuals2intervals = (visuals: CalendarTimeRange[]) => {
    const weekdays: WeekdaysToggles = clone(editInterval?.weekdays) as WeekdaysToggles
    return rangeMerge(
      visuals.reduce(
        (prev: [TimeHHMM, TimeHHMM][], cur) => prev.concat(visual2intervals(cur.start, cur.stop)), []
      )
    ).map(([start, stop]) => ({ start, stop, weekdays }))
  }

  const onDeleteInterval = () => {
    if (editIntervalIndex !== null) {
      const visuals = ruleParams.visuals
      visuals.splice(editIntervalIndex, 1)
      const intervals = visuals2intervals(visuals)

      const newParams = {
        ...ruleParams,
        visuals,
        intervals
      }

      setRuleParams(newParams)
      setEditInterval(null)
      setEditIntervalIndex(null)
      onRuleSave(newParams)
    }
  }

  const onDoneInterval = () => {

    const visuals = ruleParams.visuals.map((inv, idx) => editInterval && idx === editIntervalIndex ? editInterval : inv)
    const intervals = visuals2intervals(visuals)

    const newParams = {
      ...ruleParams,
      visuals,
      intervals
    }

    setRuleParams(newParams)
    setEditInterval(null)
    setEditIntervalIndex(null)
    onRuleSave(newParams)
  }

  // Handler for rule saving.
  const onRuleSave = async (params: FixedCalendarRuleParams) => {
    if (port === null) return

    const resp = await callAxios<Rule>({
      url: rule.id ? `/rules/${rule.id}` : '/rules',
      method: rule.id ? 'PATCH' : 'POST',
    }, {
      id: rule.id || null,
      port_number: port.port_number,
      type: rule.type,
      priority: rule.priority,
      params
    })

    if (isErrorResponse(resp)) {
      app.addError(resp)
    } else {
      const newRule = resp.data

      const resp2 = await callAxios({
        url: `/ports/${port.id}`,
        method: 'PATCH',
      }, {
        rule_id: newRule.id
      })

      if (isErrorResponse(resp2)) {
        app.addError(resp2)
      } else {
        props.addRule(device.hw_id, newRule)
        props.onUpdate({ hw_id: device.hw_id, port: { rule_id: newRule.id, port_number: port.port_number } })
      }
    }
  }

  return (
    <>
      {!editInterval && <>
        <Trans>Calendar Rules</Trans>

        <Stack spacing={1}>
          {
            ruleParams.visuals.map((interval, idx) =>
              <IntervalPanel
                key={idx}
                interval={interval}
                text={app.t('Appliance On')}
                onSelect={() => {
                  setEditInterval(interval)
                  setEditIntervalIndex(idx)
                }}
              />
            )
          }
        </Stack>

        <Box data-cy="button-Add additional time interval" onClick={onAddInterval} sx={{
          mt: 2,
          textAlign: 'center',
          textDecoration: 'underline',
          color: 'primary.main',
          cursor: 'pointer'
        }}>
          {ruleParams.visuals.length ? <Trans>Add additional time interval</Trans> : <Trans>Add time interval</Trans>}
        </Box>
      </>}

      {editInterval && <Box>
        <Trans>Calendar Rules</Trans>

        <PageTitle sx={{ mt: 2, mb: 2 }} title={app.t('Start Time ({relay})').replace('{relay}', app.t('Appliance On'))}/>
        <Box data-cy="button-Start Time Fixed Calendar">
          <TimeEdit
            value={editInterval.start}
            onChange={(time, done) =>
              setEditInterval({
                ...editInterval,
                start: time,
                stop: editInterval.stop
              })
            }
          />
        </Box>

        <PageTitle sx={{ mt: 4, mb: 2 }} title={app.t('Stop Time ({relay})').replace('{relay}', app.t('Appliance Off'))}/>
        <Box data-cy="button-Stop Time Fixed Calendar">
          <TimeEdit
            value={editInterval.stop}
            onChange={(time, done) =>
              setEditInterval({
                ...editInterval,
                stop: time,
                start: editInterval.start
              })
            }
          />
        </Box>

        <PageTitle sx={{ mt: 4 }} title={app.t('Repeat')}/>
        <DaySelector
          sx={{ mt: 2 }}
          value={editInterval.weekdays}
          onChange={(weekdays) => setEditInterval({ ...editInterval, weekdays })}
        />

        <Button data-cy="button-Done Fixed Calendar" onClick={onDoneInterval} sx={{ mt: 3 }} fullWidth><Trans>Done</Trans></Button>

        <Box onClick={onDeleteInterval} sx={{
          mt: 4,
          textAlign: 'center',
          textDecoration: 'underline',
          color: 'error.light',
          cursor: 'pointer'
        }}>
          <Trans>Delete interval</Trans>
        </Box>
      </Box>}

      {
        isDebug('rule-json') &&
          <pre>
            {JSON.stringify(ruleParams, null, 2)}
          </pre>
      }
    </>
  )
}
